import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PROVINCIE_LIJST, WERK_TYPES, typesLabel, pricingLabel } from "@/lib/jobOptions";
import { PublicNav } from "@/components/PublicNav";
import { AdSlot } from "@/components/AdSlot";
import { ArrowRight, MapPin, Calendar, BadgeCheck, Lock, Paperclip, ClipboardList } from "lucide-react";

const DASH_BY_ROLE = { monteur: "/monteur", bedrijf: "/opdrachtgever", particulier: "/opdrachtgever", admin: "/admin" };

export default function PublicJobs() {
  const { user } = useAuth();
  useSeo({
    title: "Opdrachten — Openstaande ramen- & deurenklussen | Work4you",
    description: "Bekijk alle actuele opdrachten voor ramen- en deurenmontage in België. Filter op provincie en type werk. Meld je gratis aan als professional om te reageren.",
    path: "/opdrachten",
  });
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provincie, setProvincie] = useState("alle");
  const [type, setType] = useState("alle");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (provincie !== "alle") params.provincie = provincie;
    if (type !== "alle") params.type_werk = type;
    api.get("/jobs/public", { params })
      .then((r) => setJobs(r.data.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [provincie, type]);

  const handleOpen = () => {
    if (user && user.role) navigate(DASH_BY_ROLE[user.role] || "/");
    else navigate("/inloggen?next=opdrachten");
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("nl-BE", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-jobs-page">
      <PublicNav active="opdrachten" />

      {/* Header — blauwe banner */}
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <p className="label-caps text-blue-300 mb-3 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Opdrachtenbord
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] mb-4">
            Alle openstaande <span className="text-blue-400">opdrachten</span>
          </h1>
          <p className="text-lg text-blue-100/80 max-w-2xl">
            Bekijk welke ramen- en deurenopdrachten beschikbaar zijn. Meld je gratis aan als professional om te reageren.
          </p>
        </div>
      </section>

      {/* Filters + list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <AdSlot slot="dashboard_top" publiek />
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <Select value={provincie} onValueChange={setProvincie}>
            <SelectTrigger data-testid="pub-filter-provincie" className="w-full sm:w-64 rounded-sm bg-white">
              <SelectValue placeholder="Provincie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle provincies</SelectItem>
              {PROVINCIE_LIJST.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="pub-filter-type" className="w-full sm:w-64 rounded-sm bg-white">
              <SelectValue placeholder="Type werk" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle types werk</SelectItem>
              {WERK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-slate-500" data-testid="pub-jobs-loading">Opdrachten laden…</p>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-sm border border-slate-200" data-testid="pub-jobs-empty">
            <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-semibold">Geen openstaande opdrachten met deze filters.</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="pub-jobs-grid">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`pub-job-card-${j.id}`}
                className="group bg-white border border-slate-200 rounded-sm p-5 flex flex-col hover:border-blue-400 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-display font-extrabold text-lg leading-snug">{j.titel || "Opdracht"}</h3>
                  {j.company_geverifieerd && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-sm shrink-0">
                      <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Geverifieerd
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Badge variant="secondary" className="rounded-sm">{typesLabel(j)}</Badge>
                  <Badge variant="outline" className="rounded-sm">{pricingLabel(j)}</Badge>
                </div>
                {j.beschrijving && (
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4">{j.beschrijving}</p>
                )}
                <div className="mt-auto space-y-1.5 text-sm text-slate-500">
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {j.provincie || "Provincie n.v.t."}</div>
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Start: {fmtDate(j.start_datum)}</div>
                  {j.aantal_bijlagen > 0 && (
                    <div className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> {j.aantal_bijlagen} bijlage(n)</div>
                  )}
                </div>
                <Button data-testid={`pub-job-open-${j.id}`} onClick={handleOpen}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-semibold w-full">
                  {user && user.role ? (<>Bekijk & reageer <ArrowRight className="w-4 h-4 ml-2" /></>) : (<><Lock className="w-4 h-4 mr-2" /> Inloggen om te reageren</>)}
                </Button>
              </div>
            ))}
          </div>
        )}

        {!user && (
          <div className="mt-12 bg-blue-950 text-white rounded-sm p-8 text-center" data-testid="pub-cta-register">
            <p className="font-display text-2xl font-extrabold mb-2">Wil je op opdrachten reageren?</p>
            <p className="text-blue-100/80 mb-5">Meld je gratis aan als professional en zie meteen de contactgegevens.</p>
            <Link to="/registreren?rol=monteur">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold px-8 h-12">
                Gratis aanmelden <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
